/**
 * Central resilience policy for every HTTP call the frontend makes.
 *
 * These numbers back `app/lib/api.ts`, so a change here moves every call the
 * main app makes. There is no query layer in this repo — Redux thunks and
 * sagas call axios directly — so the retry loop lives in `withRetry` rather
 * than in a client's default options.
 */

/**
 * A request that has not settled by this point is treated as failed. This is
 * the timeout `app/lib/api.ts` already applied; it stays at 60s so existing
 * long endpoints keep working. Lowering it is a one-line change here.
 *
 * The sysadmin wrapper (`app/sysadmin/api.ts`) deliberately does not use this
 * — that panel is internal-only and some of its calls run unbounded.
 */
export const DEFAULT_TIMEOUT_MS = 60000;

/** Uploads, exports and report generation legitimately outrun the default. */
export const LONG_RUNNING_TIMEOUT_MS = 120000;

const MAX_RETRIES = 3;
const BASE_RETRY_DELAY_MS = 1000;
/** Only binds if MAX_RETRIES grows; at 3 retries the exponential peaks at 4s. */
const MAX_RETRY_DELAY_MS = 8000;

/**
 * Total wall clock one logical request may consume, attempts and backoff
 * together. Without it the per-attempt timeout multiplies: four 60s attempts
 * plus backoff is ~4 minutes before the user sees an error.
 *
 * Set equal to DEFAULT_TIMEOUT_MS so a request that actually times out is not
 * retried at all — it has already spent the whole budget, and a second hang
 * would only compound it. Failures that come back quickly (a 503 in 50ms)
 * leave the budget almost untouched and still get every retry.
 */
export const TOTAL_RETRY_BUDGET_MS = 60000;

/** Below this there is no point issuing a request; it cannot finish in time. */
const MIN_ATTEMPT_BUDGET_MS = 50;

/** Status codes that stay broken however often they are retried. */
const NON_RETRYABLE_STATUS = [400, 401, 403, 404, 409, 410, 422];

export const getErrorStatus = (error: unknown): number | undefined => {
	const candidate = error as { response?: { status?: number }; status?: number } | undefined;

	return candidate?.response?.status ?? candidate?.status;
};

/**
 * A timed-out request carries no status, so it has to be recognised by name to
 * stay retryable rather than falling through as "unknown". `ERR_CANCELED` is
 * deliberately absent: that is a caller aborting on purpose, not a failure.
 */
export const isTimeoutError = (error: unknown): boolean => {
	const candidate = error as { code?: string; name?: string } | undefined;

	return (
		candidate?.code === 'ECONNABORTED' ||
		candidate?.code === 'ETIMEDOUT' ||
		candidate?.name === 'TimeoutError'
	);
};

/** A caller-initiated abort must never be retried and must never be reported as an error. */
export const isCanceledError = (error: unknown): boolean => {
	const candidate = error as { code?: string; name?: string } | undefined;

	return candidate?.code === 'ERR_CANCELED' || candidate?.name === 'AbortError';
};

/**
 * Retry only what a retry can actually fix: transport failures, timeouts, 5xx
 * and 429. Deterministic 4xx responses fail fast instead of costing the user
 * three round trips before showing the error.
 */
export const isRetryableError = (error: unknown): boolean => {
	if (isCanceledError(error)) return false;
	if (isTimeoutError(error)) return true;

	const status = getErrorStatus(error);
	if (status === undefined) return true; // network / CORS / DNS failure
	if (NON_RETRYABLE_STATUS.includes(status)) return false;
	if (status === 429) return true;

	return status >= 500;
};

/** Exponential backoff with full jitter, so retries do not arrive in lockstep. */
export const getRetryDelayMs = (attemptIndex: number): number => {
	const exponential = Math.min(BASE_RETRY_DELAY_MS * 2 ** attemptIndex, MAX_RETRY_DELAY_MS);

	return Math.round(exponential / 2 + Math.random() * (exponential / 2));
};

/** Shared by `withRetry` and by any call site that needs the same decision. */
export const shouldRetry = (failureCount: number, error: unknown): boolean =>
	failureCount < MAX_RETRIES && isRetryableError(error);

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Runs `request` and retries it with backoff while `shouldRetry` agrees.
 *
 * `request` receives the timeout its attempt may use: the configured timeout,
 * shortened to whatever is left of the budget. Checking the budget only between
 * attempts is not enough — a failure at 25s followed by an attempt that hangs
 * for the full timeout would still run past it — so the deadline has to bound
 * the in-flight attempt too. A caller that ignores the argument keeps the
 * weaker "no retry may start past the deadline" guarantee.
 *
 * `api.get` uses this automatically. A mutating verb does not: retrying a POST
 * or DELETE risks a duplicate write. Wrap a mutation in this only when the
 * endpoint is provably idempotent, and say why in a comment at the call site.
 */
export const withRetry = async <T>(
	request: (attemptTimeoutMs: number) => Promise<T>,
	configuredTimeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<T> => {
	// A call that legitimately asked for longer than the standard budget keeps
	// it — the budget never shortens a single attempt below what was configured.
	const budgetMs = Math.max(TOTAL_RETRY_BUDGET_MS, configuredTimeoutMs);
	const startedAt = Date.now();
	let lastError: unknown;

	for (let attempt = 0; ; attempt++) {
		const remainingMs = budgetMs - (Date.now() - startedAt);

		// Unreachable on the first attempt, where the whole budget is still there,
		// so lastError is always set by the time this can fire.
		if (attempt > 0 && remainingMs < MIN_ATTEMPT_BUDGET_MS) throw lastError;

		try {
			return await request(Math.min(configuredTimeoutMs, remainingMs));
		} catch (error) {
			lastError = error;
			if (!shouldRetry(attempt, error)) throw error;

			const delay = getRetryDelayMs(attempt);
			// give up rather than start a retry the budget cannot pay for
			if (Date.now() - startedAt + delay >= budgetMs) throw error;

			await sleep(delay);
		}
	}
};
